import { cn } from "@/lib/utils";
import { Star } from "lucide-react";
import React, { useEffect, useState } from "react";
import { GiMusicalNotes } from "react-icons/gi";
import { LiaThumbsDownSolid, LiaThumbsUp } from "react-icons/lia";
import { Input } from "./ui/input";
import { Separator } from "./ui/separator";

const ratingVariants = {
  default: {
    star: "text-foreground",
    emptyStar: "text-muted-foreground",
  },
  destructive: {
    star: "text-red-500",
    emptyStar: "text-red-200",
  },
  yellow: {
    star: "text-yellow-500",
    emptyStar: "text-yellow-200",
  },
};

export const Rating = ({
  feedbackId,
  setFeedbackId: setId,
  model,
  rating: initialRating,
  totalStars = 5,
  size = 20,
  fill = true,
  Icon = <Star />,
  variant = "default",
  onRatingChange,
}: {
  feedbackId: string;
  setFeedbackId: (id: string | undefined) => void;
  model?: string;
  question?: string;
  answer?: string;
  rating: number;
  totalStars?: number;
  size?: number;
  fill?: boolean;
  Icon?: React.ReactElement;
  variant?: keyof typeof ratingVariants;
  onRatingChange?: (rating: number) => void;
}) => {
  const [hoverRating, setHoverRating] = useState<number | null>(null);
  const [currentRating, setCurrentRating] = useState(initialRating);
  const [, setIsHovering] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [comment, setComment] = useState("");
  const [thumbsUpValue, setThumbsUpValue] = useState(0);

  useEffect(() => {
    setDone(false);
  }, []);

  const handleMouseEnter = (event: React.MouseEvent<HTMLDivElement>) => {
    setIsHovering(true);
    const starIndex = parseInt(
      (event.currentTarget as HTMLDivElement).dataset.starIndex || "0",
    );
    setHoverRating(starIndex);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    setHoverRating(null);
  };

  const giveFeedback = async ({
    feedbackId,
    rating,
    comment,
    summarizerName,
    thumbsUpValue,
  }: {
    feedbackId: string;
    rating: number;
    comment: string;
    summarizerName: string;
    thumbsUpValue: number;
  }) => {
    await fetch("/api/feedback", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        id: feedbackId,
        rating,
        comment,
        summarizerName,
        thumbsUpValue,
      }),
    });
  };

  const handleStarClick = async (event: React.MouseEvent<any>) => {
    const starIndex = parseInt(
      (event.currentTarget as HTMLDivElement).dataset.starIndex || "0",
    );
    setCurrentRating(starIndex);
    setHoverRating(null);
    onRatingChange?.(starIndex);
  };

  const handleClick = async (
    event: React.MouseEvent<any>,
    thumbsUpValue: number,
  ) => {
    setThumbsUpValue(thumbsUpValue);
    setLoading(true);
    await giveFeedback({
      feedbackId,
      rating: currentRating,
      comment,
      summarizerName: model ?? "",
      thumbsUpValue,
    });
    setDone(true);
    setLoading(false);
    setTimeout(() => {
      setId(undefined);
    }, 2000);
  };

  const displayRating = hoverRating ?? currentRating;
  const fullStars = Math.floor(displayRating);
  const partialStar =
    displayRating % 1 > 0 ? (
      <PartialStar
        fillPercentage={displayRating % 1}
        size={size}
        className={cn(ratingVariants[variant].star)}
        Icon={Icon}
      />
    ) : null;

  return (
    <div className={"w-full"}>
      {!done ? (
        <div className="my-1 flex max-w-fit flex-col rounded-lg bg-indigo-300 p-2 shadow-md transition-all duration-500 ease-in-out">
          <div className="mb-1 flex flex-row items-center space-x-1">
            <GiMusicalNotes className="h-4 w-4 text-indigo-700" />
            <p className="mb-1 text-xs">Rate your response</p>
          </div>
          <div className="flex flex-row items-center space-x-1 space-y-2 md:space-x-2 md:space-y-0">
            <div className="flex">
              <Input
                className="h-6 w-[175px] italic placeholder:font-sans placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-indigo-500"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Thumbs up/down to submit..."
                disabled={loading}
              />
            </div>
            {!loading && (
              <div
                className={cn("flex w-fit items-center gap-2")}
                onMouseLeave={handleMouseLeave}
              >
                <div
                  className="flex items-center"
                  onMouseEnter={handleMouseEnter}
                >
                  {[...Array(fullStars)].map((_, i) =>
                    React.cloneElement(Icon, {
                      key: i,
                      size,
                      className: cn(
                        fill ? "fill-yellow-400 stroke-1" : "fill-transparent",
                        ratingVariants[variant].star,
                      ),
                      onClick: handleStarClick,
                      onMouseEnter: handleMouseEnter,
                      "data-star-index": i + 1,
                    }),
                  )}
                  {partialStar}
                  {[
                    ...Array(
                      Math.max(
                        0,
                        totalStars - fullStars - (partialStar ? 1 : 0),
                      ),
                    ),
                  ].map((_, i) =>
                    React.cloneElement(Icon, {
                      key: i + fullStars + 1,
                      size,
                      className: cn(
                        "stroke-1",
                        ratingVariants[variant].emptyStar,
                      ),
                      onClick: handleStarClick,
                      onMouseEnter: handleMouseEnter,
                      "data-star-index": i + fullStars + 1,
                    }),
                  )}
                </div>
              </div>
            )}
            {!loading && (
              <div className="flex flex-row items-center rounded-lg p-1 ring-1">
                <LiaThumbsDownSolid
                  className={cn(
                    "h-4 w-4 text-slate-200",
                    thumbsUpValue === -1 && "text-indigo-900",
                  )}
                  onClick={async (e) => {
                    await handleClick(e, -1);
                  }}
                />
                <Separator orientation="vertical" className={cn("mx-1 h-3")} />
                <LiaThumbsUp
                  className={cn(
                    "h-4 w-4 text-slate-200",
                    thumbsUpValue === 1 && "text-indigo-900",
                  )}
                  onClick={async (e) => {
                    await handleClick(e, 1);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="my-1 flex h-16 max-w-fit flex-col items-center justify-center rounded-lg bg-indigo-300 p-2 pr-4 pt-4 shadow-md transition-all duration-500 ease-in-out">
          <div className="mx-11 mb-1">
            <div className=" flex flex-row items-center space-x-1">
              <GiMusicalNotes className="h-4 w-4 text-indigo-700" />
              <p className="mb-1 text-xs">Thank you for your feedback!</p>
            </div>
            <div className="mt-1 flex flex-row items-center justify-center space-x-4 rounded-lg ring-1">
              <Star className="h-4 w-4 text-yellow-500" />
              <p>{currentRating}</p>
              {thumbsUpValue === 1 ? (
                <LiaThumbsUp className="h-4 w-4 text-indigo-900" />
              ) : (
                <LiaThumbsDownSolid className="h-4 w-4 text-indigo-900" />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface PartialStarProps {
  fillPercentage: number;
  size: number;
  className?: string;
  Icon: React.ReactElement;
}

const PartialStar = ({ ...props }: PartialStarProps) => {
  const { fillPercentage, size, className, Icon } = props;
  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      {React.cloneElement(Icon, {
        size,
        className: cn("fill-transparent", className),
      })}
      <div
        style={{
          position: "absolute",
          top: 0,
          overflow: "hidden",
          width: `${fillPercentage * 100}%`,
        }}
      >
        {React.cloneElement(Icon, {
          size,
          className: cn("fill-current", className),
        })}
      </div>
    </div>
  );
};
